import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { client } from '@frontend/features/common/services';

type Sharing = { agency_ids: number[]; revision: number; agencies: { id: number; name: string }[] };
export function HotelConnectionSharing({
  agencyId,
  connectionId,
  name,
}: {
  agencyId: number;
  connectionId: string;
  name: string;
}) {
  const { t } = useTranslation();
  const cache = useQueryClient();
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Sharing | null>(null);
  const path = `agencies/${agencyId}/hotels/connector-accounts/${encodeURIComponent(connectionId)}/sharing`;
  const key = ['hotel-connection-sharing', agencyId, connectionId];
  const sharing = useQuery(key, async () => (await client.get<Sharing>(path)).data, { enabled: open, retry: false });
  const current = draft ?? sharing.data;
  const save = useMutation(
    async () => {
      if (!current) throw new Error('Missing sharing settings');
      await client.put(path, { agency_ids: current.agency_ids, revision: current.revision });
    },
    {
      onSuccess: async () => {
        setOpen(false);
        setDraft(null);
        await Promise.all([
          cache.invalidateQueries(key),
          cache.invalidateQueries(['hotel-booking-permissions']),
          cache.invalidateQueries(['hotel-search-contexts']),
        ]);
      },
    },
  );
  return (
    <>
      <Button
        onClick={() => {
          setDraft(null);
          save.reset();
          setOpen(true);
        }}
      >
        {t('hotelSharing.manage')}
      </Button>
      <Dialog open={open} onClose={() => !save.isLoading && setOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          {t('hotelSharing.manage')} · {name}
        </DialogTitle>
        <DialogContent>
          <Stack gap={2} pt={1}>
            <Typography>{t('hotelSharing.hint')}</Typography>
            {sharing.isError && <Alert severity="error">{t('hotelConnections.error')}</Alert>}
            {current && (
              <>
                <Autocomplete
                  multiple
                  options={current.agencies}
                  value={current.agencies.filter((agency) => current.agency_ids.includes(agency.id))}
                  disabled={save.isLoading}
                  getOptionLabel={(agency) => agency.name}
                  isOptionEqualToValue={(a, b) => a.id === b.id}
                  onChange={(_, value) => setDraft({ ...current, agency_ids: value.map((agency) => agency.id) })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('hotelSharing.agencies')}
                      helperText={t(current.agency_ids.length ? 'hotelSharing.shared' : 'hotelSharing.exclusive')}
                    />
                  )}
                />
                <Typography variant="caption">{t('hotelSharing.existingBookings')}</Typography>
              </>
            )}
            {save.isError && (
              <Alert severity="error">
                {t('hotelSharing.saveError')}
                <Button
                  onClick={() => {
                    setDraft(null);
                    save.reset();
                    sharing.refetch();
                  }}
                >
                  {t('hotelConnections.retry')}
                </Button>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={save.isLoading} onClick={() => setOpen(false)}>
            {t('hotelConnections.close')}
          </Button>
          <Button
            disabled={!current || sharing.isFetching || sharing.isError || save.isLoading}
            onClick={() => save.mutate()}
          >
            {t('hotelConnections.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
